import { type ReactNode, useState } from "react"

import { Link, useMatchRoute } from "@tanstack/react-router"
import { ChevronLeftIcon, InfoIcon, LogOutIcon } from "lucide-react"
import { useStore } from "zustand"

import { CompanySwitcher } from "@/components/layout/CompanySwitcher"
import {
  type DashboardNavItem,
  isInternalNavItem,
  useDashboardNavigation,
} from "@/components/layout/dashboard-navigation"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { store } from "@/lib/store"
import { cn, isEmbed } from "@/lib/utils"

export function Sidebar({ onLogout }: { onLogout?: () => void }) {
  const userRole = useStore(store, (state) => state.userRole)
  const { topItems, bottomItems } = useDashboardNavigation()

  if (!userRole || !["customer", "lender", "partner"].includes(userRole)) {
    return null
  }

  return (
    <BaseSidebar
      topLinks={topItems}
      bottomLinks={bottomItems}
      onLogout={onLogout}
    />
  )
}

function BaseSidebar({
  topLinks: top,
  bottomLinks: bottom,
  onLogout,
}: {
  topLinks: DashboardNavItem[]
  bottomLinks: DashboardNavItem[]
  onLogout?: () => void
}) {
  const [isCollapsed, setIsCollapsed] = useState(isEmbed())
  const matchRoute = useMatchRoute()

  const NavLink = ({ item }: { item: DashboardNavItem }) => {
    const Icon = item.icon
    const RightIcon = item.rightIcon
    const isActive =
      isInternalNavItem(item) && !item.disabled
        ? Boolean(matchRoute({ ...item.navLinkOptions, fuzzy: true }))
        : false

    const sharedClassName = cn(
      "group flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-nowrap transition-all",
      isActive ? "bg-primary ml-2 text-white" : "text-muted hover:bg-muted/10",
      isCollapsed && "justify-center px-2",
      item.disabled && "text-muted/50 cursor-default",
    )

    const content: ReactNode = (
      <>
        <Icon className="size-5" />
        {!isCollapsed && (
          <div className="flex gap-1">
            <span>{item.name}</span>
            {item.tooltip && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <InfoIcon className="h-3 w-3" />
                </TooltipTrigger>
                <TooltipContent sideOffset={4}>{item.tooltip}</TooltipContent>
              </Tooltip>
            )}
          </div>
        )}
        {RightIcon && <RightIcon className="size-4" />}
      </>
    )

    if (isInternalNavItem(item)) {
      return (
        <Link
          {...item.navLinkOptions}
          className={sharedClassName}
          title={isCollapsed ? item.name : undefined}
          disabled={item.disabled}
        >
          {content}
        </Link>
      )
    }

    return (
      <a
        href={item.href}
        className={sharedClassName}
        title={isCollapsed ? item.name : undefined}
        aria-disabled={item.disabled}
        onClick={(event) => {
          if (item.disabled) {
            event.preventDefault()
          }
        }}
        {...(item.newTab
          ? { rel: "noopener noreferrer", target: "_blank" }
          : {})}
      >
        {content}
      </a>
    )
  }

  return (
    <div
      role="complementary"
      className={cn(
        "relative flex h-full flex-col bg-gray-50/65 px-4 py-6 backdrop-blur-sm transition-all duration-300",
        isCollapsed ? "w-16" : "w-64",
      )}
    >
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="absolute top-4 -right-3 flex h-6 w-6 items-center justify-center rounded-full bg-white shadow-md transition-transform hover:scale-110"
      >
        <ChevronLeftIcon
          className={cn(
            "h-4 w-4 transition-transform",
            isCollapsed && "rotate-180",
          )}
        />
      </button>

      <CompanySwitcher isCollapsed={isCollapsed} />

      <nav className="min-h-0 flex-1 space-y-2 overflow-x-hidden overflow-y-auto">
        {top.map((item) => (
          <NavLink key={item.id} item={item} />
        ))}
      </nav>

      <div
        className={cn(
          "rounded-xl transition-all",
          !isCollapsed &&
            (bottom.length > 0 || onLogout) &&
            "bg-white p-4 shadow-sm",
        )}
      >
        <nav className="space-y-1">
          {bottom.map((item) => (
            <NavLink key={item.id} item={item} />
          ))}
          {onLogout && (
            <button
              type="button"
              onClick={onLogout}
              title={isCollapsed ? "Logout" : undefined}
              className={cn(
                "group flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-nowrap transition-all",
                "text-muted hover:bg-muted/10",
                isCollapsed && "justify-center px-2",
              )}
            >
              <LogOutIcon className="size-5" />
              {!isCollapsed && <span>Logout</span>}
            </button>
          )}
        </nav>
      </div>
    </div>
  )
}
